import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Stack,
  Paper,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowParams,
  GridToolbar,
} from '@mui/x-data-grid';
import {
  Search as SearchIcon,
  Assignment as AssignIcon,
  Build as TechnicianIcon,
  Devices as DevicesIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  SimCard as SimCardIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { apiService } from '../../services/api';
import { Device, User } from '../../types';

export const DeviceAssignment: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [assignDialog, setAssignDialog] = useState<{
    open: boolean;
    device?: Device;
  }>({ open: false });
  const [selectedTechnician, setSelectedTechnician] = useState('');

  // API calls
  const {
    data: notInstalledDevices,
    loading: devicesLoading,
    error: devicesError,
    execute: fetchNotInstalledDevices,
  } = useApi(() => apiService.getNotInstalledDevices(), { immediate: false });

  const {
    data: techniciansData,
    execute: fetchTechnicians,
  } = useApi(() => apiService.getTechnicians(), { immediate: false });

  // Extraer el array de usuarios del objeto de respuesta
  const technicians = (techniciansData as any)?.users || [];

  useEffect(() => {
    fetchNotInstalledDevices();
    fetchTechnicians();
  }, [fetchNotInstalledDevices, fetchTechnicians]);

  const handleAssignToTechnician = (device: Device) => {
    setAssignDialog({
      open: true,
      device,
    });
  };

  const handleConfirmAssignment = async () => {
    if (!assignDialog.device || !selectedTechnician) return;

    try {
      await apiService.assignDeviceToTechnician(assignDialog.device._id, selectedTechnician);
      setAssignDialog({ open: false });
      setSelectedTechnician('');
      fetchNotInstalledDevices();
    } catch (error) {
      console.error('Error assigning device:', error);
    }
  };

  const handleSyncDevices = async () => {
    try {
      await apiService.syncDevices();
      setTimeout(() => {
        fetchNotInstalledDevices();
      }, 2000);
    } catch (error) {
      console.error('Error syncing devices:', error);
    }
  };

  const getDeviceStatusIcon = (device: Device) => {
    if (device.status === 'active') {
      return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} />;
    } else {
      return <ErrorIcon sx={{ color: 'error.main', fontSize: 16 }} />;
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'imei',
      headerName: 'IMEI',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'warning.main' }}>
            <DevicesIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {params.value}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {getDeviceStatusIcon(params.row)}
              <Typography variant="caption" color="text.secondary">
                {params.row.status || 'inactive'}
              </Typography>
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: 'name',
      headerName: 'Nombre',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value || 'Sin nombre'}
        </Typography>
      ),
    },
    {
      field: 'brand_model',
      headerName: 'Modelo',
      width: 150,
      renderCell: (params) => {
        const model = params.row.model;
        const brand = params.row.brand;
        // Si el modelo es genérico/unknown, mostrar el nombre o N/A
        const displayModel = (model && model !== 'Unknown' && model !== 'N/A') ? model : (params.row.name || 'N/A');
        const displayBrand = (brand && brand !== 'Generic') ? brand : '';
        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {displayModel}
            </Typography>
            {displayBrand && (
              <Typography variant="caption" color="text.secondary">
                {displayBrand}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'simCard',
      headerName: 'SIM Card',
      width: 120,
      renderCell: (params) => (
        params.row.simCardData ? (
          <Tooltip title={`ICCID: ${params.row.simCardData.iccid}`}>
            <Chip
              icon={<SimCardIcon />}
              label={params.row.simCardData.status}
              size="small"
              color={params.row.simCardData.status === 'active' ? 'success' : 'default'}
              variant="outlined"
            />
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Sin SIM
          </Typography>
        )
      ),
    },
    {
      field: 'lastSync',
      headerName: 'Última Sincronización',
      width: 180,
      renderCell: (params) => (
        <Typography variant="caption" color="text.secondary">
          {params.value ? new Date(params.value).toLocaleString('es-DO') : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      width: 100,
      getActions: (params: GridRowParams<Device>) => [
        <GridActionsCellItem
          icon={<TechnicianIcon />}
          label="Asignar a Técnico"
          onClick={() => handleAssignToTechnician(params.row)}
        />,
      ],
    },
  ];

  // Extraer el array de dispositivos del objeto de respuesta
  const devicesArray = (notInstalledDevices as any)?.data || (notInstalledDevices as any) || [];

  const filteredDevices = Array.isArray(devicesArray) ? devicesArray.filter((device: any) =>
    searchQuery === '' ||
    device.imei?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    device.name?.toLowerCase().includes(searchQuery.toLowerCase())
  ) : [];

  // Debug: Ver qué datos están llegando
  console.log('🔍 DeviceAssignment - notInstalledDevices:', notInstalledDevices);
  console.log('🔍 DeviceAssignment - devicesArray:', devicesArray);
  console.log('🔍 DeviceAssignment - filteredDevices:', filteredDevices);
  console.log('🔍 DeviceAssignment - technicians:', technicians);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Asignación de Dispositivos
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Dispositivos disponibles en el grupo "Not_Installed"
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Sincronizar con Traccar y Simbase">
            <Button
              variant="outlined"
              startIcon={<SyncIcon />}
              onClick={handleSyncDevices}
            >
              Sincronizar
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchNotInstalledDevices}
          >
            Actualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<DevicesIcon />}
            onClick={() => navigate('/devices/new')}
          >
            Registrar GPS
          </Button>
        </Box>
      </Box>

      {/* Stats Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <InventoryIcon sx={{ fontSize: 48, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'warning.main' }}>
                {filteredDevices.length}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Dispositivos Disponibles
              </Typography>
              <Typography variant="body2" color="text.secondary">
                En grupo "Not_Installed"
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <SimCardIcon sx={{ fontSize: 48, color: 'info.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'info.main' }}>
                {filteredDevices.filter((d: Device) => d.simCardData).length}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Con SIM Card
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Conectados a Simbase
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TechnicianIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 600, color: 'success.main' }}>
                {technicians.length}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                Técnicos Disponibles
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Para asignación
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Workflow Instruction */}
      <Paper sx={{ p: 3, mb: 4, backgroundColor: 'primary.50' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
          📋 Instrucciones de Workflow
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          1. Los dispositivos aquí mostrados están sincronizados en tiempo real desde Traccar (grupo "Not_Installed")
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          2. Cada dispositivo puede tener una SIM card asociada desde Simbase (relación por IMEI)
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          3. Asigna dispositivos a técnicos para que procedan con la instalación
        </Typography>
        <Typography variant="body2">
          4. Una vez instalados, los técnicos asignarán los dispositivos a clientes finales
        </Typography>
      </Paper>

      {/* Device List */}
      <Card>
        <CardContent>
          {/* Search */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar por IMEI, nombre o marca..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 400 }}
            />
          </Box>

          {devicesError ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              Error cargando dispositivos: {devicesError}
            </Alert>
          ) : null}

          <DataGrid
            rows={filteredDevices}
            columns={columns}
            loading={devicesLoading}
            pagination
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            getRowId={(row) => row._id}
            slots={{
              toolbar: GridToolbar,
            }}
            sx={{
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
              },
              border: 0,
              minHeight: 400,
            }}
          />
        </CardContent>
      </Card>

      {/* Assignment Dialog */}
      <Dialog
        open={assignDialog.open}
        onClose={() => setAssignDialog({ open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Asignar Dispositivo a Técnico
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {assignDialog.device && (
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Dispositivo:</strong> {assignDialog.device.name || assignDialog.device.imei}
                </Typography>
                <Typography variant="body2">
                  <strong>IMEI:</strong> {assignDialog.device.imei}
                </Typography>
                <Typography variant="body2">
                  <strong>Marca/Modelo:</strong> {assignDialog.device.brand} {assignDialog.device.model}
                </Typography>
                {assignDialog.device.simCardData && (
                  <Typography variant="body2">
                    <strong>SIM Card:</strong> {assignDialog.device.simCardData.iccid} ({assignDialog.device.simCardData.status})
                  </Typography>
                )}
              </Alert>
            )}

            <FormControl fullWidth>
              <InputLabel>Técnico</InputLabel>
              <Select
                value={selectedTechnician}
                label="Técnico"
                onChange={(e) => setSelectedTechnician(e.target.value)}
              >
                {technicians.map((tech: User) => (
                  <MenuItem key={tech._id} value={tech._id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TechnicianIcon fontSize="small" />
                      <Box>
                        <Typography variant="body2">{tech.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {tech.email}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog({ open: false })}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmAssignment}
            disabled={!selectedTechnician}
            startIcon={<AssignIcon />}
          >
            Asignar a Técnico
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceAssignment;